// APIEndpointModal.tsx - API Endpoint Configuration Template
import React, { useState, useEffect, useRef } from 'react';
import { APIEndpointModalProps, APIEndpointType, AuthType } from '../../types';



// Environment-aware API configuration
const getApiBaseUrl = () => {
    // First check if explicitly set (for Docker and GCP)
    if (process.env.REACT_APP_API_BASE_URL) {
        return process.env.REACT_APP_API_BASE_URL;
    }

    // Development fallback
    if (process.env.NODE_ENV === "development") {
        return "http://localhost:8000";
    }

    // Production: use hostname replacement only as last resort
    const hostname = window.location.hostname;

    // Check if we're on localhost (production build running locally)
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return "http://localhost:8000";
    }

    // GCP production - replace frontend-alpha with orchestrator
    const apiHost = hostname.replace('frontend-alpha', 'orchestrator');
    return `https://${apiHost}`;
};

const API_BASE_URL = getApiBaseUrl();






const APIEndpointModal: React.FC<APIEndpointModalProps> = ({
    isOpen,
    onClose,
    endpoint,
    onSave,
    onTest
}) => {
    const [formData, setFormData] = useState({
        name: '',
        type: 'REST' as APIEndpointType,
        url: '',
        authentication: {
            type: 'none' as AuthType,
            credentials: {
                api_key: '',
                username: '',
                password: '',
                token: '',
                additional_params: {} as Record<string, string>
            }
        },
        configuration: {
            database_name: '',
            schema: '',
            headers: {} as Record<string, string>,
            timeout: 30,
            domain: ''  // Add domain field
        },
        isActive: true,
        description: ''
    });

    const [isTestingConnection, setIsTestingConnection] = useState(false);
    const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
    const [headersText, setHeadersText] = useState('');
    const [additionalParamsText, setAdditionalParamsText] = useState('');

    const [activeTab, setActiveTab] = useState(0);

    // Application management states
    const [applicationName, setApplicationName] = useState<string>('');
    const [existingApplications, setExistingApplications] = useState<string[]>([]);
    const [useExistingApp, setUseExistingApp] = useState<boolean>(true);
    const [selectedApplication, setSelectedApplication] = useState<string>('');

    const [uploadedDocuments, setUploadedDocuments] = useState<any[]>([]);
    const [parsedResults, setParsedResults] = useState<any[]>([]);
    const [isUploadingDocs, setIsUploadingDocs] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    // Adding state for templates
    const [availableTemplates, setAvailableTemplates] = useState<any[]>([]);
    const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);

    const [useTemplate, setUseTemplate] = useState<boolean>(false);

    const [templateSource, setTemplateSource] = useState<'official' | 'system' | 'user_defined'>('system');
    const [templateLibrary, setTemplateLibrary] = useState<any>({ official: [], system: [], user_defined: [] });
    const [isDownloading, setIsDownloading] = useState(false);

    const [uploadMethod, setUploadMethod] = useState<'file' | 'url'>('file');
    const [customTemplateName, setCustomTemplateName] = useState('');
    const [customTemplateVersion, setCustomTemplateVersion] = useState('');
    const [selectedTemplateFile, setSelectedTemplateFile] = useState<File | null>(null);
    const [templateUrl, setTemplateUrl] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const templateFileInputRef = useRef<HTMLInputElement>(null);

    const [availableDomains, setAvailableDomains] = useState([]);


    // Adding this new state for checkbox selections RAMANA 10.09.2025
    const [templateSources, setTemplateSources] = useState({
        official: false,
        system: false,
        custom: false
    });

    // Added this new state for tracking multiple template sources RAMANA 10.09.2025
    const [selectedTemplateSources, setSelectedTemplateSources] = useState({
        official: false,
        system: false,
        user_defined: false
    });


    // Track selected providers
    const [selectedProviders, setSelectedProviders] = useState({
        tmf_forum: false,
        '3gpp': false,
        psd2_berlin: false,
        open_banking_uk: false,
        iso20022: false,
        hl7_fhir: false,
        schema_org: false,
        payment_standards: false
    });


    // Determine if this is a platform integration or test target API
    //const isPlatformIntegration = ['Database', 'Jira', 'Confluence', 'Databricks', 'Bitbucket'].includes(formData.type);
    const isPlatformIntegration = false; // Since we moved all platform integrations to Settings

    // Dynamic tabs based on type
    const tabs = isPlatformIntegration
        ? ['API Details', 'Test Connection']
        : ['API Details', 'Domain & Context', 'Documentation', 'Review & Generate'];

    const [tabsCompleted, setTabsCompleted] = useState(
        isPlatformIntegration
            ? { details: false, test: false }
            : { details: false, domain: false, documentation: false, review: false }
    );

    // Add drag and drop handlers
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleDocumentUpload(e.dataTransfer.files);
        }
    };


    const loadDomainTemplates = async (domain: string) => {
        try {
            // Load the full template library
            const response = await fetch(`${API_BASE_URL}/api/templates/library`);
            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    // Store the full library
                    setTemplateLibrary(data.library);
                    //console.log('Template library loaded:', data.library);

                    // Filter templates for current domain based on selected source
                    const domainTemplates = data.library[templateSource]?.filter(
                        (t: any) => t.domain === domain
                    ) || [];
                    setAvailableTemplates(domainTemplates);
                }
            }
        } catch (error) {
            //console.error('Failed to load templates:', error);
        }
    };

    const handleTemplateSelect = async (templateId: string) => {
        setSelectedTemplates(prev =>
            prev.includes(templateId)
                ? prev.filter(id => id !== templateId)
                : [...prev, templateId]
        );

        if (templateId && formData.configuration.domain) {
            try {
                const response = await fetch(`${API_BASE_URL}/api/templates/${formData.configuration.domain}/${templateId}`);
                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.template) {
                        //console.log('Template selected:', data.template);
                        // Optionally pre-fill form with template data
                        // For example, you could set base_path or other fields
                        if (data.template.base_path) {
                            setFormData(prev => ({
                                ...prev,
                                url: prev.url || `https://your-server.com${data.template.base_path}`
                            }));
                        }
                    }
                }
            } catch (error) {
                console.error('Failed to load template details:', error);
            }
        }
    };


    const handleTemplateDownload = async () => {
        if (!selectedTemplate) return;

        const template = availableTemplates.find((t: any) => t.id === selectedTemplate);
        if (!template) return;

        setIsDownloading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/templates/download`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    template_id: template.id,
                    domain: formData.configuration.domain,
                    source_url: template.source_url
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    alert('Template downloaded successfully!');
                    // Reload templates to update download status
                    loadDomainTemplates(formData.configuration.domain);
                }
            }
        } catch (error) {
            console.error('Failed to download template:', error);
            alert('Failed to download template');
        } finally {
            setIsDownloading(false);
        }
    };


    const handleTemplateFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setSelectedTemplateFile(file);
        }
    };

    const handleCustomTemplateUpload = async () => {
        if (!customTemplateName || !customTemplateVersion) {
            alert('Please provide template name and version');
            return;
        }

        if (uploadMethod === 'file' && !selectedTemplateFile) {
            alert('Please select a file to upload');
            return;
        }

        if (uploadMethod === 'url' && !templateUrl) {
            alert('Please provide a URL');
            return;
        }

        setIsUploading(true);
        try {
            let response;

            if (uploadMethod === 'file') {
                const uploadData = new FormData();
                uploadData.append('file', selectedTemplateFile!);
                uploadData.append('name', customTemplateName);
                uploadData.append('version', customTemplateVersion);
                uploadData.append('domain', formData.configuration.domain);
                uploadData.append('application_id', selectedApplication || '');

                response = await fetch(`${API_BASE_URL}/api/templates/upload`, {
                    method: 'POST',
                    body: uploadData
                });
            } else {
                response = await fetch(`${API_BASE_URL}/api/templates/upload-url`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        url: templateUrl,
                        name: customTemplateName,
                        version: customTemplateVersion,
                        domain: formData.configuration.domain,
                        application_id: selectedApplication || ''
                    })
                });
            }

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    alert('Template uploaded successfully!');
                    // Reset upload form
                    setCustomTemplateName('');
                    setCustomTemplateVersion('');
                    setSelectedTemplateFile(null);
                    setTemplateUrl('');
                    if (templateFileInputRef.current) {
                        templateFileInputRef.current.value = '';
                    }
                    // Reload templates
                    loadDomainTemplates(formData.configuration.domain);
                    // Switch to system view to see uploaded template
                    setTemplateSource('user_defined');
                }
            } else {
                alert('Failed to upload template');
            }
        } catch (error) {
            console.error('Failed to upload template:', error);
            alert('Failed to upload template');
        } finally {
            setIsUploading(false);
        }
    };

    // Document upload handler
    const handleDocumentUpload = async (files: FileList) => {
        setIsUploadingDocs(true);

        const uploadFormData = new FormData();
        for (let i = 0; i < files.length; i++) {
            uploadFormData.append('files', files[i]);
        }

        // Add API context
        uploadFormData.append('api_context', JSON.stringify({
            api_name: formData.name || 'New API',
            api_type: formData.type || 'REST'
        }));

        try {
            const response = await fetch(`${API_BASE_URL}/api/documents/upload-for-api`, {
                method: 'POST',
                body: uploadFormData
            });

            if (response.ok) {
                const result = await response.json();
                setUploadedDocuments(result.uploaded_documents);
                setParsedResults(result.parsed_results);

                // Update form data with parsed endpoints if found
                const apiSpec = result.parsed_results.find((r: any) => r.type === 'api_spec');
                if (apiSpec && apiSpec.endpoints) {
                    setFormData(prev => ({
                        ...prev,
                        configuration: {
                            ...prev.configuration,
                            endpoints: apiSpec.endpoints,
                            schemas: apiSpec.schemas
                        }
                    }));
                }
            } else {
                alert('Failed to upload documents');
            }
        } catch (error) {
            console.error('Upload error:', error);
            alert('Error uploading documents');
        } finally {
            setIsUploadingDocs(false);
        }
    };

    // Initialize form when editing existing endpoint
    // Initialize form when editing existing endpoint
    useEffect(() => {
        //console.log('APIEndpointModal - endpoint prop on mount:', endpoint);

        if (endpoint) {
            // Handle both formats - from APIManagementWidget and other sources
            const endpointData = endpoint.endpoint || endpoint;
            //console.log('APIEndpointModal - endpointData:', endpointData);

            setFormData({
                name: endpointData.name,
                type: endpointData.type || 'REST',
                url: endpointData.base_url || endpointData.url,
                authentication: {
                    type: endpointData.auth_type || 'none',
                    credentials: {
                        api_key: '',
                        username: '',
                        password: '',
                        token: '',
                        additional_params: {}
                    }
                },
                configuration: {
                    database_name: '',
                    schema: '',
                    headers: {},
                    timeout: 30,
                    domain: endpointData.domain || ''
                },
                isActive: endpointData.is_active !== undefined ? endpointData.is_active : true,
                description: endpointData.description || ''
            });

            // Convert headers and params to text for editing
            setHeadersText('{}');
            setAdditionalParamsText('{}');

            // Load the application if it exists
            if (endpointData.application_id || endpointData.application_name) {
                setUseExistingApp(true);
                setSelectedApplication(endpointData.application_name || '');
            }

            // Load uploaded documents if they exist
            if (endpointData.documents) {
                setUploadedDocuments(endpointData.documents);
            }

            // Reset template states
            setAvailableTemplates([]);
            setSelectedTemplates([]);

            setUseTemplate(false);

            // Load templates if domain exists
            if (endpointData.domain) {
                loadDomainTemplates(endpointData.domain);
            }
        } else {
            // Reset form for new endpoint
            setFormData({
                name: '',
                type: 'REST',
                url: '',
                authentication: {
                    type: 'none',
                    credentials: {
                        api_key: '',
                        username: '',
                        password: '',
                        token: '',
                        additional_params: {}
                    }
                },
                configuration: {
                    database_name: '',
                    schema: '',
                    headers: {},
                    timeout: 30,
                    domain: ''
                },
                isActive: true,
                description: ''
            });
            setHeadersText('{}');
            setAdditionalParamsText('{}');
            setTestResult(null);

            // Reset template states
            setAvailableTemplates([]);
            setSelectedTemplates([]);
            setUseTemplate(false);

            // Reset application states
            setUseExistingApp(true);
            setSelectedApplication('');
            setApplicationName('');

            // Reset document states
            setUploadedDocuments([]);
            setParsedResults([]);
        }
    }, [endpoint, isOpen]);


    useEffect(() => {
        const loadDomains = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/templates/library`);
                const data = await response.json();

                if (data.success && data.library) {
                    const domainSet = new Set();
                    data.library.system.forEach(template => {
                        if (template.domain) {
                            domainSet.add(template.domain);
                        }
                    });

                    const domainOptions = Array.from(domainSet).map(domain => ({
                        id: domain,
                        name: domain === 'sap' ? 'SAP Enterprise' :
                            domain === 'ecommerce' ? 'E-commerce' :
                                domain === 'finance' ? 'Finance & Banking' :
                                    domain === 'healthcare' ? 'Healthcare' :
                                        domain === 'telecommunications' ? 'Telecommunications' :
                                            domain.charAt(0).toUpperCase() + domain.slice(1)
                    }));

                    setAvailableDomains(domainOptions);
                }
            } catch (error) {
                console.error('Failed to load domains:', error);
                setAvailableDomains([
                    { id: 'ecommerce', name: 'E-commerce' },
                    { id: 'finance', name: 'Finance & Banking' },
                    { id: 'healthcare', name: 'Healthcare' },
                    { id: 'telecommunications', name: 'Telecommunications' },
                    { id: 'sap', name: 'SAP Enterprise' },
                    { id: 'general', name: 'General Purpose' }
                ]);
            }
        };

        loadDomains();
    }, []);

    // Reload templates when template source changes
    useEffect(() => {
        if (formData.configuration.domain && useTemplate) {
            // Filter templates for current domain based on selected source
            const domainTemplates = templateLibrary[templateSource]?.filter(
                (t: any) => t.domain === formData.configuration.domain
            ) || [];
            setAvailableTemplates(domainTemplates);
        }
    }, [templateSource, templateLibrary, formData.configuration.domain, useTemplate]);


    // Function to fetch existing applications
    const fetchExistingApplications = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/db/applications`);
            if (response.ok) {
                const apps = await response.json();
                setExistingApplications(apps.map((app: any) => app.name));
                if (apps.length === 0) {
                    setUseExistingApp(false);
                }
            } else if (response.status === 404) {
                // No applications endpoint yet, default to create new
                //console.log('Applications endpoint not available yet');
                setUseExistingApp(false);
            }
        } catch (error) {
            console.error('Failed to fetch applications:', error);
            setUseExistingApp(false);
        }
    };


    const handleTestConnection = async () => {
        if (!formData.name.trim() || !formData.url.trim()) {
            setTestResult({ success: false, message: 'Please fill in name and URL/IP before testing' });
            return;
        }

        setIsTestingConnection(true);
        setTestResult(null);

        try {
            // Parse JSON fields
            let headers = {};
            let additionalParams = {};

            try {
                headers = JSON.parse(headersText);
                additionalParams = JSON.parse(additionalParamsText);
            } catch (e) {
                setTestResult({ success: false, message: 'Invalid JSON in headers or additional parameters' });
                setIsTestingConnection(false);
                return;
            }

            const testEndpoint = {
                ...formData,
                id: 'test',
                configuration: {
                    ...formData.configuration,
                    headers
                },
                authentication: {
                    ...formData.authentication,
                    credentials: {
                        ...formData.authentication.credentials,
                        additional_params: additionalParams
                    }
                }
            };

            if (onTest) {
                const success = await onTest(testEndpoint);
                setTestResult({
                    success,
                    message: success ? 'Connection successful!' : 'Connection failed. Check your configuration.'
                });
            } else {
                // Simulate test for demo
                await new Promise(resolve => setTimeout(resolve, 2000));
                setTestResult({
                    success: Math.random() > 0.3,
                    message: Math.random() > 0.3 ? 'Connection successful!' : 'Connection failed. Check your configuration.'
                });
            }
        } catch (error) {
            setTestResult({ success: false, message: `Test failed: ${error}` });
        } finally {
            setIsTestingConnection(false);
        }
    };

    const handleSave = () => {

        console.log('=== DEBUG TEMPLATE SAVE ===');
        console.log('useTemplate:', useTemplate);
        console.log('selectedTemplates:', selectedTemplates);
        console.log('selectedTemplateSources:', selectedTemplateSources);
        console.log('domain:', formData.configuration?.domain);


        if (!formData.name.trim()) {
            alert('Please provide an endpoint name');
            return;
        }

        if (!formData.url.trim()) {
            alert('Please provide a URL/IP address');
            return;
        }

        try {
            // Parse JSON fields
            const headers = JSON.parse(headersText);
            const additionalParams = JSON.parse(additionalParamsText);
            //  THESE LINES to get parsed data
            const apiSpec = parsedResults.find(r => r.type === 'api_spec');

            onSave({
                id: endpoint?.id,
                name: formData.name,
                type: formData.type,
                url: formData.url,
                authentication: {
                    type: formData.authentication.type,
                    credentials: {
                        ...formData.authentication.credentials,
                        additional_params: additionalParams
                    }
                },
                configuration: {
                    ...formData.configuration,
                    headers
                },
                isActive: formData.isActive,
                description: formData.description,
                isPlatformIntegration,
                application_name: useExistingApp ? selectedApplication : applicationName,
                custom_documents: uploadedDocuments.map(d => d.document_id),
                parsed_endpoints: apiSpec?.endpoints || [],
                parsed_schemas: apiSpec?.schemas || {},
                selected_templates: selectedTemplates,
                use_template: useTemplate
            });

            onClose();
        } catch (e) {
            alert('Invalid JSON in headers or additional parameters');
        }
    };


    // Load templates when domain changes
    useEffect(() => {
        if (formData.configuration.domain) {
            loadDomainTemplates(formData.configuration.domain);
        }
    }, [formData.configuration.domain]);



    // Fetch applications when modal opens
    useEffect(() => {
        if (isOpen) {
            fetchExistingApplications();
        }
    }, [isOpen]); // Only fetch when modal opens/closes


    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ width: '900px', maxWidth: '95vw' }}>
                <h2>🔗 {endpoint ? 'Edit' : 'Create'} {isPlatformIntegration ? 'Integration' : 'API Endpoint'}</h2>

                {/* Tab Navigation */}
                <div style={{
                    display: 'flex',
                    borderBottom: '2px solid #e0e0e0',
                    marginBottom: '1.5rem'
                }}>
                    {tabs.map((tab, index) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(index)}
                            style={{
                                flex: 1,
                                padding: '0.75rem',
                                border: 'none',
                                background: activeTab === index ? '#007bff' : 'transparent',
                                color: activeTab === index ? 'white' : '#666',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: activeTab === index ? 'bold' : 'normal',
                                transition: 'all 0.2s'
                            }}
                        >
                            {tab}
                            {tabsCompleted[Object.keys(tabsCompleted)[index]] && activeTab !== index && ' ✔'}
                        </button>
                    ))}
                </div>

                {/* Tab Content Container */}
                <div style={{ minHeight: '400px' }}>
                    {activeTab === 0 && (
                        <div>
                            {/* Endpoint Type Selection */}
                            <div className="control-group">
                                <label>Endpoint Type</label>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', marginTop: '0.5rem' }}>
                                    {['REST', 'GraphQL', 'SOAP', 'WebSocket'].map(type => (
                                        <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}>
                                            <input
                                                type="radio"
                                                checked={formData.type === type}
                                                onChange={() => setFormData(prev => ({ ...prev, type: type as APIEndpointType }))}
                                            />
                                            {type}
                                        </label>
                                    ))}
                                    {['gRPC', 'OpenAPI', 'AsyncAPI', 'Custom'].map(type => (
                                        <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}>
                                            <input
                                                type="radio"
                                                checked={formData.type === type}
                                                onChange={() => setFormData(prev => ({ ...prev, type: type as APIEndpointType }))}
                                            />
                                            {type}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* 1. Endpoint Name */}
                            <div className="control-group">
                                <label>1. Endpoint Name <span style={{ color: 'red' }}>*</span></label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="e.g., ProductionDatabase, JiraAPI, PaymentGateway"
                                    style={{ width: '100%' }}
                                />
                            </div>

                            {/* Application Selection/Creation */}
                            <div className="control-group" style={{ marginBottom: '1.5rem' }}>
                                <label>Application *</label>

                                {/* Radio buttons to choose between existing or new */}
                                <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
                                    <label>
                                        <input
                                            type="radio"
                                            checked={useExistingApp}
                                            onChange={() => setUseExistingApp(true)}
                                            disabled={existingApplications.length === 0}
                                        />
                                        Select Existing
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            checked={!useExistingApp}
                                            onChange={() => setUseExistingApp(false)}
                                        />
                                        Create New
                                    </label>
                                </div>

                                {/* Show dropdown or text input based on selection */}
                                {useExistingApp ? (
                                    <select
                                        value={selectedApplication}
                                        onChange={(e) => setSelectedApplication(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            fontSize: '1rem',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px'
                                        }}
                                    >
                                        <option value="">-- Select Application --</option>
                                        {existingApplications.map(app => (
                                            <option key={app} value={app}>{app}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        value={applicationName}
                                        onChange={(e) => setApplicationName(e.target.value)}
                                        placeholder="e.g., BillingSystem, UniverseCare, myApp"
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            fontSize: '1rem',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px'
                                        }}
                                    />
                                )}

                                <small style={{ color: '#666' }}>
                                    Group related agents and APIs under this application
                                </small>
                            </div>


                            {/* 2. URL/IP Address */}
                            <div className="control-group">
                                <label>2. URL/IP Address <span style={{ color: 'red' }}>*</span></label>
                                <input
                                    type="text"
                                    value={formData.url}
                                    onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                                    placeholder={
                                        formData.type === 'Database' ? 'postgresql://localhost:5432' :
                                            formData.type === 'Jira' ? 'https://yourcompany.atlassian.net' :
                                                'https://api.example.com or 192.168.1.100:8080'
                                    }
                                    style={{ width: '100%' }}
                                />
                            </div>

                            {/* 3. Authentication Type */}
                            <div className="control-group">
                                <label>3. Authentication Type</label>
                                <select
                                    value={formData.authentication.type}
                                    onChange={(e) => setFormData(prev => ({
                                        ...prev,
                                        authentication: { ...prev.authentication, type: e.target.value as AuthType }
                                    }))}
                                    style={{ width: '100%' }}
                                >
                                    <option value="none">None</option>
                                    <option value="api_key">API Key</option>
                                    <option value="basic">Basic Auth (Username/Password)</option>
                                    <option value="oauth2">OAuth</option>
                                    <option value="bearer">Bearer Token</option>
                                </select>
                            </div>

                            {/* 4. Credentials */}
                            <div className="control-group">
                                <label>4. Credentials</label>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                                    {formData.authentication.type === 'api_key' && (
                                        <div>
                                            <label style={{ fontSize: '0.9rem' }}>API Key</label>
                                            <input
                                                type="password"
                                                value={formData.authentication.credentials.api_key}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    authentication: {
                                                        ...prev.authentication,
                                                        credentials: { ...prev.authentication.credentials, api_key: e.target.value }
                                                    }
                                                }))}
                                                placeholder="Enter your API key"
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                    )}

                                    {(formData.authentication.type === 'basic' || formData.authentication.type === 'oauth2') && (
                                        <>
                                            <div>
                                                <label style={{ fontSize: '0.9rem' }}>Username</label>
                                                <input
                                                    type="text"
                                                    value={formData.authentication.credentials.username}
                                                    onChange={(e) => setFormData(prev => ({
                                                        ...prev,
                                                        authentication: {
                                                            ...prev.authentication,
                                                            credentials: { ...prev.authentication.credentials, username: e.target.value }
                                                        }
                                                    }))}
                                                    placeholder="Username"
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '0.9rem' }}>Password</label>
                                                <input
                                                    type="password"
                                                    value={formData.authentication.credentials.password}
                                                    onChange={(e) => setFormData(prev => ({
                                                        ...prev,
                                                        authentication: {
                                                            ...prev.authentication,
                                                            credentials: { ...prev.authentication.credentials, password: e.target.value }
                                                        }
                                                    }))}
                                                    placeholder="Password"
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                        </>
                                    )}

                                    {formData.authentication.type === 'bearer' && (
                                        <div style={{ gridColumn: 'span 2' }}>
                                            <label style={{ fontSize: '0.9rem' }}>Bearer Token</label>
                                            <input
                                                type="password"
                                                value={formData.authentication.credentials.token}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    authentication: {
                                                        ...prev.authentication,
                                                        credentials: { ...prev.authentication.credentials, token: e.target.value }
                                                    }
                                                }))}
                                                placeholder="Enter bearer token"
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* 5. Configuration */}
                            <div className="control-group">
                                <label>5. Configuration</label>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                                    {formData.type === 'Database' && (
                                        <>
                                            <div>
                                                <label style={{ fontSize: '0.9rem' }}>Database Name</label>
                                                <input
                                                    type="text"
                                                    value={formData.configuration.database_name}
                                                    onChange={(e) => setFormData(prev => ({
                                                        ...prev,
                                                        configuration: { ...prev.configuration, database_name: e.target.value }
                                                    }))}
                                                    placeholder="e.g., testwerk_db"
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '0.9rem' }}>Schema</label>
                                                <input
                                                    type="text"
                                                    value={formData.configuration.schema}
                                                    onChange={(e) => setFormData(prev => ({
                                                        ...prev,
                                                        configuration: { ...prev.configuration, schema: e.target.value }
                                                    }))}
                                                    placeholder="e.g., public"
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                        </>
                                    )}

                                    <div>
                                        <label style={{ fontSize: '0.9rem' }}>Timeout (seconds)</label>
                                        <input
                                            type="number"
                                            value={formData.configuration.timeout}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                configuration: { ...prev.configuration, timeout: parseInt(e.target.value) || 30 }
                                            }))}
                                            min="1"
                                            max="300"
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Headers */}
                            <div className="control-group">
                                <label>Headers (JSON format)</label>
                                <textarea
                                    value={headersText}
                                    onChange={(e) => setHeadersText(e.target.value)}
                                    placeholder='{"Content-Type": "application/json", "X-API-Version": "v1"}'
                                    rows={3}
                                    style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.9rem' }}
                                />
                            </div>

                            {/* Additional Parameters */}
                            {formData.authentication.type !== 'none' && (
                                <div className="control-group">
                                    <label>Additional Authentication Parameters (JSON format)</label>
                                    <textarea
                                        value={additionalParamsText}
                                        onChange={(e) => setAdditionalParamsText(e.target.value)}
                                        placeholder='{"scope": "read:user", "grant_type": "client_credentials"}'
                                        rows={2}
                                        style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.9rem' }}
                                    />
                                </div>
                            )}

                            {/* Description */}
                            <div className="control-group">
                                <label>Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder="Describe what this endpoint is used for..."
                                    rows={2}
                                    style={{ width: '100%' }}
                                />
                            </div>

                            {/* Active Status */}
                            <div className="control-group">
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                        type="checkbox"
                                        checked={formData.isActive}
                                        onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                                    />
                                    Enable endpoint immediately
                                </label>
                            </div>

                            {/* Test Connection Result */}
                            {testResult && (
                                <div style={{
                                    padding: '1rem',
                                    borderRadius: '4px',
                                    backgroundColor: testResult.success ? '#d4edda' : '#f8d7da',
                                    border: `1px solid ${testResult.success ? '#c3e6cb' : '#f5c6cb'}`,
                                    color: testResult.success ? '#155724' : '#721c24'
                                }}>
                                    {testResult.message}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Tab 1: Domain & Context (for test target APIs) */}
                    {activeTab === 1 && !isPlatformIntegration && (
                        <div>
                            <h3>Domain & Context</h3>
                            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                                Select your API domain to get intelligent test generation tailored to your use case.
                            </p>

                            <div className="control-group">
                                <label>API Domain</label>
                                <select
                                    value={formData.configuration.domain || ''}
                                    onChange={(e) => setFormData(prev => ({
                                        ...prev,
                                        configuration: { ...prev.configuration, domain: e.target.value }
                                    }))}
                                    style={{ width: '100%' }}
                                >
                                    <option value="">-- Select Domain --</option>
                                    {availableDomains.map(domain => (
                                        <option key={domain.id} value={domain.id}>
                                            {domain.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* New template section */}
                            {formData.configuration.domain && (
                                <div style={{ marginTop: '2rem' }}>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={useTemplate}
                                            onChange={(e) => setUseTemplate(e.target.checked)}
                                        />
                                        Use Industry Standard Template
                                    </label>

                                    {useTemplate && (
                                        <div style={{ marginTop: '1rem' }}>
                                            <label>Template Source (select multiple):</label>
                                            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}> {/* Remove flexDirection: 'column' */}
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedTemplateSources.official}
                                                        onChange={(e) => {
                                                            setSelectedTemplateSources(prev => ({ ...prev, official: e.target.checked }));
                                                            if (e.target.checked) setTemplateSource('official');
                                                        }}
                                                    />
                                                    Official Standards
                                                </label>
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedTemplateSources.system}
                                                        onChange={(e) => {
                                                            setSelectedTemplateSources(prev => ({ ...prev, system: e.target.checked }));
                                                            if (e.target.checked) setTemplateSource('system');
                                                        }}
                                                    />
                                                    System Library
                                                </label>
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedTemplateSources.user_defined}
                                                        onChange={(e) => {
                                                            setSelectedTemplateSources(prev => ({ ...prev, user_defined: e.target.checked }));
                                                            if (e.target.checked) setTemplateSource('user_defined');
                                                        }}
                                                    />
                                                    Upload Custom
                                                </label>
                                            </div>

                                            {/* Show provider selection when official is selected */}
                                            {useTemplate && selectedTemplateSources.official && (
                                                <div style={{
                                                    marginTop: '1rem',
                                                    padding: '1rem',
                                                    border: '1px solid #007bff',
                                                    borderRadius: '8px',
                                                    backgroundColor: '#f0f8ff'
                                                }}>
                                                    <h4>Select Standard Providers for {formData.configuration.domain}:</h4>
                                                    {formData.configuration.domain === 'telecommunications' && (
                                                        <div>
                                                            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    name="tmf_forum"
                                                                    checked={selectedProviders.tmf_forum}
                                                                    onChange={(e) => setSelectedProviders(prev => ({ ...prev, tmf_forum: e.target.checked }))}
                                                                /> TMF Forum
                                                            </label>
                                                            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    name="3gpp"
                                                                    checked={selectedProviders['3gpp']}
                                                                    onChange={(e) => setSelectedProviders(prev => ({ ...prev, '3gpp': e.target.checked }))}
                                                                /> 3GPP Standards
                                                            </label>
                                                        </div>
                                                    )}
                                                    {formData.configuration.domain === 'finance' && (
                                                        <div>
                                                            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    name="psd2_berlin"
                                                                    checked={selectedProviders.psd2_berlin}
                                                                    onChange={(e) => setSelectedProviders(prev => ({ ...prev, psd2_berlin: e.target.checked }))}
                                                                /> PSD2 (Berlin Group)
                                                            </label>
                                                            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    name="open_banking_uk"
                                                                    checked={selectedProviders.open_banking_uk}
                                                                    onChange={(e) => setSelectedProviders(prev => ({ ...prev, open_banking_uk: e.target.checked }))}
                                                                /> Open Banking UK
                                                            </label>
                                                            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    name="iso20022"
                                                                    checked={selectedProviders.iso20022}
                                                                    onChange={(e) => setSelectedProviders(prev => ({ ...prev, iso20022: e.target.checked }))}
                                                                /> ISO 20022
                                                            </label>
                                                        </div>
                                                    )}
                                                    {formData.configuration.domain === 'healthcare' && (
                                                        <div>
                                                            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    name="hl7_fhir"
                                                                    checked={selectedProviders.hl7_fhir}
                                                                    onChange={(e) => setSelectedProviders(prev => ({ ...prev, hl7_fhir: e.target.checked }))}
                                                                /> HL7 FHIR R4
                                                            </label>
                                                        </div>
                                                    )}
                                                    {formData.configuration.domain === 'ecommerce' && (
                                                        <div>
                                                            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    name="schema_org"
                                                                    checked={selectedProviders.schema_org}
                                                                    onChange={(e) => setSelectedProviders(prev => ({ ...prev, schema_org: e.target.checked }))}
                                                                /> Schema.org
                                                            </label>
                                                            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    name="payment_standards"
                                                                    checked={selectedProviders.payment_standards}
                                                                    onChange={(e) => setSelectedProviders(prev => ({ ...prev, payment_standards: e.target.checked }))}
                                                                /> Payment Standards
                                                            </label>
                                                        </div>
                                                    )}

                                                    <div style={{ marginTop: '1rem' }}>
                                                        <input
                                                            type="file"
                                                            accept=".json,.yaml,.yml"
                                                            multiple
                                                            style={{ display: 'none' }}
                                                            id="official-standards-upload"
                                                            onChange={async (e) => {
                                                                if (e.target.files && e.target.files.length > 0) {
                                                                    //console.log('Files selected:', e.target.files);

                                                                    // Get selected provider IDs
                                                                    const activeProviders = Object.keys(selectedProviders)
                                                                        .filter(key => selectedProviders[key]);

                                                                    //console.log('Selected providers:', activeProviders);

                                                                    // TODO: Upload files with provider context
                                                                    const formData = new FormData();
                                                                    for (let i = 0; i < e.target.files.length; i++) {
                                                                        formData.append('files', e.target.files[i]);
                                                                    }
                                                                    formData.append('domain', formData.configuration.domain);
                                                                    formData.append('selected_providers', JSON.stringify(activeProviders));
                                                                    formData.append('application_id', selectedApplication || applicationName);

                                                                    // Upload will be handled here
                                                                }
                                                            }}
                                                        />
                                                        <button
                                                            onClick={() => document.getElementById('official-standards-upload')?.click()}
                                                            style={{
                                                                padding: '0.5rem 1rem',
                                                                backgroundColor: '#007bff',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '4px',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            Browse Standard Files
                                                        </button>
                                                        <p style={{ marginTop: '0.5rem', fontSize: '0.9em', color: '#666' }}>
                                                            Upload OpenAPI/Swagger specifications for selected providers
                                                        </p>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Show system library dropdown when system is selected */}
                                            {useTemplate && selectedTemplateSources.system && (
                                                <div style={{ marginTop: '1rem' }}>
                                                    <label>System Library Templates:</label>
                                                    <div style={{ marginTop: '1rem' }}>
                                                        <label>System Library Templates (select multiple):</label>
                                                        <div style={{
                                                            display: 'grid',
                                                            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                                                            gap: '0.75rem',
                                                            marginTop: '0.75rem'
                                                        }}>
                                                            {templateLibrary.system
                                                                .filter((t: any) => t.domain === formData.configuration.domain || t.domain === 'general')
                                                                .map((template: any) => (
                                                                    <label key={template.id} style={{
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        gap: '0.25rem',
                                                                        padding: '0.75rem',
                                                                        border: `2px solid ${selectedTemplates.includes(template.id) ? '#007bff' : '#ddd'}`,
                                                                        borderRadius: '6px',
                                                                        backgroundColor: selectedTemplates.includes(template.id) ? '#e3f2fd' : '#fff',
                                                                        cursor: 'pointer',
                                                                        transition: 'all 0.2s'
                                                                    }}>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={selectedTemplates.includes(template.id)}
                                                                                onChange={(e) => {
                                                                                    if (e.target.checked) {
                                                                                        setSelectedTemplates([...selectedTemplates, template.id]);
                                                                                    } else {
                                                                                        setSelectedTemplates(selectedTemplates.filter(id => id !== template.id));
                                                                                    }
                                                                                }}
                                                                            />
                                                                            <span style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                                                                                {template.name}
                                                                            </span>
                                                                            {template.version && (
                                                                                <span style={{ fontSize: '0.8rem', color: '#666' }}>
                                                                                    v{template.version}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <span style={{ fontSize: '0.8rem', color: '#666', marginLeft: '1.5rem' }}>
                                                                            Standard compliance template
                                                                        </span>
                                                                    </label>
                                                                ))}
                                                        </div>
                                                    </div>
                                                    {templateLibrary.system.filter((t: any) => t.domain === formData.configuration.domain).length === 0 && (
                                                        <p style={{ marginTop: '0.5rem', fontSize: '0.9em', color: '#666' }}>
                                                            Showing general templates. Domain-specific templates: {
                                                                templateLibrary.system.filter((t: any) => t.domain === formData.configuration.domain).length
                                                            }
                                                        </p>
                                                    )}
                                                </div>
                                            )}


                                            {/* Show upload interface when user_defined is selected */}
                                            {useTemplate && selectedTemplateSources.user_defined && (
                                                <div style={{
                                                    marginTop: '1rem',
                                                    padding: '1rem',
                                                    border: '2px dashed #ccc',
                                                    borderRadius: '8px',
                                                    backgroundColor: '#f9f9f9'
                                                }}>
                                                    <h4>Upload Custom Template</h4>

                                                    <div style={{ marginBottom: '1rem' }}>
                                                        <label>Template Name:</label>
                                                        <input
                                                            type="text"
                                                            placeholder="e.g., Custom Payment API"
                                                            style={{ width: '100%', padding: '0.5rem' }}
                                                            onChange={(e) => setCustomTemplateName(e.target.value)}
                                                        />
                                                    </div>

                                                    <div style={{ marginBottom: '1rem' }}>
                                                        <label>Version:</label>
                                                        <input
                                                            type="text"
                                                            placeholder="e.g., 1.0.0"
                                                            style={{ width: '100%', padding: '0.5rem' }}
                                                            onChange={(e) => setCustomTemplateVersion(e.target.value)}
                                                        />
                                                    </div>

                                                    <div style={{ marginBottom: '1rem' }}>
                                                        <label>Upload Method:</label>
                                                        <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                                                            <label>
                                                                <input
                                                                    type="radio"
                                                                    name="uploadMethod"
                                                                    value="file"
                                                                    checked={uploadMethod === 'file'}
                                                                    onChange={(e) => setUploadMethod(e.target.value as 'file' | 'url')}
                                                                />
                                                                File Upload
                                                            </label>
                                                            <label>
                                                                <input
                                                                    type="radio"
                                                                    name="uploadMethod"
                                                                    value="url"
                                                                    checked={uploadMethod === 'url'}
                                                                    onChange={(e) => setUploadMethod(e.target.value as 'file' | 'url')}
                                                                />
                                                                URL
                                                            </label>
                                                        </div>
                                                    </div>

                                                    {uploadMethod === 'file' ? (
                                                        <div>
                                                            <input
                                                                type="file"
                                                                ref={templateFileInputRef}
                                                                accept=".json,.yaml,.yml"
                                                                onChange={handleTemplateFileUpload}
                                                                style={{ display: 'none' }}
                                                            />
                                                            <button
                                                                onClick={() => templateFileInputRef.current?.click()}
                                                                style={{
                                                                    padding: '0.5rem 1rem',
                                                                    backgroundColor: '#007bff',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '4px',
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                Choose File
                                                            </button>
                                                            {selectedTemplateFile && (
                                                                <span style={{ marginLeft: '1rem' }}>
                                                                    {selectedTemplateFile.name}
                                                                </span>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div>
                                                            <input
                                                                type="url"
                                                                placeholder="https://example.com/api-spec.json"
                                                                style={{ width: '100%', padding: '0.5rem' }}
                                                                onChange={(e) => setTemplateUrl(e.target.value)}
                                                            />
                                                        </div>
                                                    )}

                                                    <button
                                                        onClick={handleCustomTemplateUpload}
                                                        disabled={isUploading}
                                                        style={{
                                                            marginTop: '1rem',
                                                            padding: '0.5rem 1rem',
                                                            backgroundColor: '#28a745',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: isUploading ? 'not-allowed' : 'pointer',
                                                            opacity: isUploading ? 0.6 : 1
                                                        }}
                                                    >
                                                        {isUploading ? 'Uploading...' : 'Upload Template'}
                                                    </button>
                                                </div>
                                            )}





                                        </div>
                                    )}

                                </div>
                            )}


                        </div>
                    )}

                    {/* Tab 1: Test Connection (for platform integrations) */}
                    {activeTab === 1 && isPlatformIntegration && (
                        <div>
                            <h3>Test Connection</h3>
                            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                                Test your {formData.type} integration to ensure connectivity.
                            </p>

                            <div style={{
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                padding: '1.5rem',
                                backgroundColor: '#f8f9fa'
                            }}>
                                <h4>Connection Details</h4>
                                <div style={{ marginBottom: '1rem' }}>
                                    <strong>Type:</strong> {formData.type}
                                </div>
                                <div style={{ marginBottom: '1rem' }}>
                                    <strong>URL:</strong> {formData.url || 'Not configured'}
                                </div>
                                <div style={{ marginBottom: '1rem' }}>
                                    <strong>Authentication:</strong> {formData.authentication.type}
                                </div>
                            </div>

                            {testResult && (
                                <div style={{
                                    marginTop: '1rem',
                                    padding: '1rem',
                                    borderRadius: '4px',
                                    backgroundColor: testResult.success ? '#d4edda' : '#f8d7da',
                                    border: `1px solid ${testResult.success ? '#c3e6cb' : '#f5c6cb'}`,
                                    color: testResult.success ? '#155724' : '#721c24'
                                }}>
                                    {testResult.message}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Tab 2: Documentation (only for test target APIs) */}
                    {activeTab === 2 && !isPlatformIntegration && (
                        <div>
                            <h3>API Documentation</h3>
                            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                                Upload API documentation to enable intelligent test generation.
                            </p>

                            {/* Upload Area */}
                            <div
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                style={{
                                    border: `2px dashed ${dragOver ? '#1976d2' : '#ccc'}`,
                                    borderRadius: '8px',
                                    padding: '2rem',
                                    textAlign: 'center',
                                    backgroundColor: dragOver ? '#f0f8ff' : '#fafafa',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                }}
                                onClick={() => !isUploadingDocs && fileInputRef.current?.click()}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    accept=".json,.yaml,.yml,.pdf,.docx,.txt,.md"
                                    style={{ display: 'none' }}
                                    onChange={(e) => e.target.files && handleDocumentUpload(e.target.files)}
                                />

                                {isUploadingDocs ? (
                                    <div>
                                        <p>📤 Uploading and parsing documents...</p>
                                        <p style={{ fontSize: '0.9rem', color: '#666' }}>Please wait</p>
                                    </div>
                                ) : (
                                    <>
                                        <p style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>
                                            📄 Drop files here or click to browse
                                        </p>
                                        <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
                                            Supported: OpenAPI/Swagger (JSON/YAML), Requirements (PDF/DOCX), Postman Collections
                                        </p>
                                        <button className="pushable push-secondary btn-sm" type="button">
                                            <span className="shadow"></span>
                                            <span className="edge"></span>
                                            <span className="front">Browse Files</span>
                                        </button>
                                    </>
                                )}
                            </div>

                            {/* Uploaded Documents List */}
                            {uploadedDocuments.length > 0 && (
                                <div style={{ marginTop: '1.5rem' }}>
                                    <h4>📚 Uploaded Documents ({uploadedDocuments.length})</h4>
                                    <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '0.5rem' }}>
                                        {uploadedDocuments.map((doc, index) => (
                                            <div key={index} style={{
                                                padding: '0.5rem',
                                                borderBottom: index < uploadedDocuments.length - 1 ? '1px solid #f0f0f0' : 'none',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}>
                                                <span>📎 {doc.filename}</span>
                                                <span style={{ color: '#666', fontSize: '0.85rem' }}>
                                                    {doc.file_type} • {(doc.file_size / 1024).toFixed(1)}KB
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Parsed Results Display */}
                            {parsedResults.length > 0 && (
                                <div style={{ marginTop: '1.5rem' }}>
                                    <h4>✅ Parsed Information</h4>
                                    {parsedResults.map((result, index) => (
                                        <div key={index} style={{
                                            padding: '0.75rem',
                                            backgroundColor: '#e3f2fd',
                                            borderRadius: '4px',
                                            marginBottom: '0.5rem'
                                        }}>
                                            <strong>{result.filename}</strong>
                                            {result.type === 'api_spec' && result.endpoints && (
                                                <div style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                                                    • Found {result.endpoints.length} endpoints<br />
                                                    • {Object.keys(result.schemas || {}).length} schemas defined<br />
                                                    • Auth: {result.auth_schemes?.join(', ') || 'None'}
                                                </div>
                                            )}
                                            {result.type === 'requirements' && (
                                                <div style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                                                    • {result.business_rules?.length || 0} business rules extracted<br />
                                                    • {result.test_scenarios?.length || 0} test scenarios found
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Tab 3: Review & Generate (only for test target APIs) */}
                    {activeTab === 3 && !isPlatformIntegration && (
                        <div>
                            <h3>Review & Generate Tests</h3>
                            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                                Review your API configuration before generating test cases.
                            </p>

                            <div style={{
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                padding: '1.5rem',
                                backgroundColor: '#f8f9fa'
                            }}>
                                <h4>Configuration Summary</h4>
                                <div style={{ marginBottom: '1rem' }}>
                                    <strong>API Name:</strong> {formData.name || 'Not set'}
                                </div>
                                <div style={{ marginBottom: '1rem' }}>
                                    <strong>URL:</strong> {formData.url || 'Not set'}
                                </div>
                                <div style={{ marginBottom: '1rem' }}>
                                    <strong>Type:</strong> {formData.type}
                                </div>
                                <div style={{ marginBottom: '1rem' }}>
                                    <strong>Domain:</strong> {formData.configuration.domain || 'Not selected'}
                                </div>
                                <div style={{ marginBottom: '1rem' }}>
                                    <strong>Authentication:</strong> {formData.authentication.type}
                                </div>
                            </div>

                            {/* Templates & Standards Summary */}
                            {(selectedTemplateSources.official || selectedTemplateSources.system || selectedTemplateSources.user_defined) && (
                                <div style={{
                                    border: '1px solid #ddd',
                                    borderRadius: '8px',
                                    padding: '1.5rem',
                                    backgroundColor: '#f8f9fa',
                                    marginTop: '1rem'
                                }}>
                                    <h4>Templates & Standards</h4>
                                    {selectedTemplateSources.official && (
                                        <div style={{ marginBottom: '0.5rem' }}>
                                            <strong>Official Standards:</strong>
                                            {Object.entries(selectedProviders)
                                                .filter(([_, selected]) => selected)
                                                .map(([provider]) => provider.replace('_', ' '))
                                                .join(', ') || ' None selected'}
                                        </div>
                                    )}

                                    {selectedTemplateSources.system && selectedTemplates.length > 0 && (
                                        <div style={{ marginBottom: '0.5rem' }}>
                                            <strong>System Templates:</strong> {selectedTemplates.map(id =>
                                                templateLibrary.system.find(t => t.id === id)?.name
                                            ).join(', ')}
                                        </div>
                                    )}
                                    {selectedTemplateSources.user_defined && customTemplateName && (
                                        <div style={{ marginBottom: '0.5rem' }}>
                                            <strong>Custom Template:</strong> {customTemplateName}
                                        </div>
                                    )}
                                </div>
                            )}



                        </div>
                    )}
                </div>

                {/* Modal Actions */}
                <div className="modal-actions">
                    {activeTab === 0 && (
                        <>
                            <button onClick={onClose} className="pushable push-secondary">
                                <span className="shadow"></span>
                                <span className="edge"></span>
                                <span className="front">Cancel</span>
                            </button>
                            <button
                                onClick={() => setActiveTab(1)}
                                className="pushable push-primary"
                                disabled={!formData.name.trim() || !formData.url.trim()}
                            >
                                <span className="shadow"></span>
                                <span className="edge"></span>
                                <span className="front">
                                    {isPlatformIntegration ? 'Next: Test' : 'Next: Domain'}
                                </span>
                            </button>
                        </>
                    )}

                    {activeTab === 1 && !isPlatformIntegration && (
                        <>
                            <button onClick={() => setActiveTab(0)} className="pushable push-secondary">
                                <span className="shadow"></span>
                                <span className="edge"></span>
                                <span className="front">← Back</span>
                            </button>
                            <button onClick={() => setActiveTab(2)} className="pushable push-primary">
                                <span className="shadow"></span>
                                <span className="edge"></span>
                                <span className="front">Next: Documentation</span>
                            </button>
                        </>
                    )}

                    {activeTab === 1 && isPlatformIntegration && (
                        <>
                            <button onClick={() => setActiveTab(0)} className="pushable push-secondary">
                                <span className="shadow"></span>
                                <span className="edge"></span>
                                <span className="front">← Back</span>
                            </button>
                            <button
                                onClick={handleTestConnection}
                                className="pushable push-warning"
                                disabled={isTestingConnection}
                            >
                                <span className="shadow"></span>
                                <span className="edge"></span>
                                <span className="front">
                                    {isTestingConnection ? 'Testing...' : 'Test Connection'}
                                </span>
                            </button>
                            <button onClick={handleSave} className="pushable push-success">
                                <span className="shadow"></span>
                                <span className="edge"></span>
                                <span className="front">Save Integration</span>
                            </button>
                        </>
                    )}

                    {activeTab === 2 && !isPlatformIntegration && (
                        <>
                            <button onClick={() => setActiveTab(1)} className="pushable push-secondary">
                                <span className="shadow"></span>
                                <span className="edge"></span>
                                <span className="front">← Back</span>
                            </button>
                            <button onClick={() => setActiveTab(3)} className="pushable push-primary">
                                <span className="shadow"></span>
                                <span className="edge"></span>
                                <span className="front">Next: Review</span>
                            </button>
                        </>
                    )}

                    {activeTab === 3 && !isPlatformIntegration && (
                        <>
                            <button onClick={() => setActiveTab(2)} className="pushable push-secondary">
                                <span className="shadow"></span>
                                <span className="edge"></span>
                                <span className="front">← Back</span>
                            </button>
                            <button
                                onClick={handleTestConnection}
                                className="pushable push-warning"
                                disabled={isTestingConnection}
                            >
                                <span className="shadow"></span>
                                <span className="edge"></span>
                                <span className="front">Test Connection</span>
                            </button>
                            <button
                                onClick={handleSave}
                                className="pushable push-success"
                            >
                                <span className="shadow"></span>
                                <span className="edge"></span>
                                <span className="front">Save & Generate Tests</span>
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default APIEndpointModal;